import React from "react";

import CloseIcon from "@mui/icons-material/Close";
import { Avatar, Chip, useTheme } from "@mui/material";

export interface Practitioner {
  id: string;
  name: string;
  initials: string;
}

interface StyledPractitionerChipProps {
  practitioner: Practitioner;
  onDelete: (event: React.SyntheticEvent) => void;
}

/**
 * Styled chip component for displaying practitioner with avatar.
 * Used in PractitionerAutocomplete for rendering selected practitioners.
 *
 * @example
 * <StyledPractitionerChip
 *   practitioner={{ id: "1", name: "Dr. Sarah Johnson", initials: "SJ" }}
 *   onDelete={() => handleRemove("1")}
 * />
 */
export const StyledPractitionerChip: React.FC<StyledPractitionerChipProps> = ({ practitioner, onDelete }) => {
  const { palette, spacing, typography } = useTheme();

  return (
    <Chip
      avatar={
        <Avatar
          sx={{
            width: spacing(3),
            height: spacing(3),
            borderRadius: "50%",
            backgroundColor: palette.grey[400],
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: palette.common.white,
            fontSize: typography.caption.fontSize,
            fontWeight: typography.subtitle2.fontWeight,
          }}
        >
          {practitioner.initials}
        </Avatar>
      }
      label={practitioner.name}
      deleteIcon={<CloseIcon />}
      onDelete={onDelete}
      sx={{
        height: spacing(4),
        mr: 1,
        borderRadius: spacing(2),
        border: "1px solid",
        borderColor: palette.grey[300],
        backgroundColor: "transparent",
        "& .MuiChip-avatar": {
          width: spacing(2.75),
          height: spacing(2.75),
          color: palette.common.white,
          marginLeft: spacing(0.75),
          marginRight: spacing(0.25),
        },
        "& .MuiChip-label": {
          fontSize: typography.body2.fontSize,
          fontWeight: typography.body1.fontWeight,
          color: palette.text.primary,
          letterSpacing: "0.0731px",
          px: 1,
        },
        "& .MuiChip-deleteIcon": {
          width: spacing(2.125),
          height: spacing(2.125),
          borderRadius: "50%",
          backgroundColor: palette.grey[400],
          color: palette.action.active,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          marginRight: spacing(0.75),
          marginLeft: spacing(0.25),
          transition: "background-color 0.2s ease, color 0.2s ease",
          "&:hover": {
            backgroundColor: palette.grey[500],
            color: palette.grey[100],
          },
        },
      }}
    />
  );
};
